import { useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import {
  formatEnumLabel,
  formatLegalAccountType,
  formatTransactionType,
} from '@betterment/shared-components/src/utils/StringHelpers';

import {
  AccountTypeFilterOption,
  ActivityAdvisorType,
  ActivityItemTypeFilterOption,
  DateRangeOption,
} from '../../../graphql/schema';
import { ActivityFiltersDocument, ActivityFiltersQuery } from '../graphql/query.activityFilters.generated';

type FilterOption<T> = {
  value: T;
  text?: string;
};
export type TimeframeItem = FilterOption<DateRangeOption>;
export type TransactionTypeItem = FilterOption<ActivityItemTypeFilterOption>;
export type AdvisorTypeItem = FilterOption<ActivityAdvisorType>;
export type ClientItem = FilterOption<string>;
export type LegalAccountTypeItem = FilterOption<AccountTypeFilterOption>;

export type ActivityItemFilter = {
  legalAccountType: LegalAccountTypeItem[];
  timeframe?: TimeframeItem;
  transactionType: TransactionTypeItem[];
  advisorType: AdvisorTypeItem;
  clients: ClientItem[];
  startTimeInclusive?: Date | null;
  endTimeExclusive?: Date | null;
};

export type FilterOptionsMap = {
  clients: ClientItem[];
  legalAccountType: LegalAccountTypeItem[];
  timeframe: TimeframeItem[];
  transactionType: TransactionTypeItem[];
  advisorType: AdvisorTypeItem[];
  startTimeInclusive?: Date | null;
  endTimeExclusive?: Date | null;
};

export type FilterKey = keyof ActivityItemFilter;

interface FilterOptions {
  filterOptions?: FilterOptionsMap;
  filters?: ActivityItemFilter;
  setFilters: (filters: ActivityItemFilter) => void;
  loading: boolean;
  error: Error | undefined;
}

interface UseFilterOptionsProps {
  householdId?: string;
}

export const useFilterOptions = ({ householdId }: UseFilterOptionsProps): FilterOptions => {
  const { data, loading, error } = useGraphQLQuery<ActivityFiltersQuery>(ActivityFiltersDocument, {
    variables: {
      householdId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const filterOptions: FilterOptionsMap | undefined = data && {
    advisorType: (data.advisorTypeOptions ?? []).map(val => ({
      text: formatEnumLabel(val),
      value: val,
    })),
    timeframe: (data.activityDateRangeOptions ?? []).map(val => ({
      text: formatEnumLabel(val),
      value: val,
    })),
    transactionType: (data.activityItemTypeOptions ?? []).map(val => ({
      text: formatTransactionType(val),
      value: val,
    })),
    legalAccountType: Object.values(AccountTypeFilterOption).map(val => ({
      text: formatLegalAccountType(val),
      value: val,
    })),
    clients: (data.activityItemClientOptions ?? []).map(val => ({
      text: val.legalFullName,
      value: val.id,
    })),
  };

  const [filters, setFilters] = useState<ActivityItemFilter>();

  if (!filters && filterOptions) {
    setFilters({
      legalAccountType: filterOptions.legalAccountType,
      timeframe: filterOptions.timeframe[0],
      transactionType: filterOptions.transactionType,
      advisorType: filterOptions.advisorType[0],
      clients: filterOptions.clients,
      startTimeInclusive: null,
      endTimeExclusive: null,
    });
  }

  return {
    filterOptions,
    filters,
    setFilters,
    loading,
    error,
  };
};
